import { useState } from "react";
import { useParams, Link } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api } from "../lib/api";
import { Code } from "../components/primitives/Code";
import { CopyButton } from "../components/primitives/CopyButton";
import { Button } from "../components/primitives/Button";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";
import { PageHelp } from "../components/PageHelp";
import { PageHeader } from "../components/primitives/PageHeader";
import { TechnicalDetails } from "../components/primitives/TechnicalDetails";
import { vendorLabel } from "../lib/friendly-labels";
import { ButtonLink } from "../components/primitives/ButtonLink";

/**
 * One connection: which app it reaches, whether it is reachable now, and the
 * pipelines depending on it. Testing it is Tier 0 — a read against the
 * vendor, nothing written.
 */
export function Connection() {
  const { id } = useParams<{ id: string }>();
  const q = usePolling(() => api.getConnectorInstance(id!), 30000);
  const connectors = usePolling(() => api.listConnectors(), 60000);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail?: string } | null>(null);
  const [testRunning, setTestRunning] = useState(false);
  const [setupResult, setSetupResult] = useState<{ ok: boolean; detail?: string } | null>(null);
  const [setupRunning, setSetupRunning] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  if (q.status === "loading" || !q.data || connectors.status === "loading") return <Pulse />;

  const inst = q.data.instance;
  const c = (connectors.data?.connectors ?? []).find(
    (x: { connectorType: string }) => x.connectorType === inst.connectorType,
  );
  const webhook = c?.ingest?.webhook;
  const hasTestConnection = Array.isArray(c?.capabilities) && c.capabilities.includes("testConnection");
  const hasSetup = Array.isArray(c?.capabilities) && c.capabilities.includes("setup");
  const gatewayBase = import.meta.env.VITE_WEBHOOK_GATEWAY_BASE_URL ?? "";
  const pushUrl = webhook
    ? `${gatewayBase}/h/${inst.id}`
    : null;
  const secretName = `jump-pipelines/connectors/${inst.connectorType}/${inst.id}`;

  /** Ask the API to reach the vendor with these credentials and show the verdict. */
  async function runTest() {
    setTestRunning(true);
    setTestResult(null);
    try {
      const res = await api.testConnection(id!);
      setTestResult(res);
    } catch (err: unknown) {
      setTestResult({ ok: false, detail: err instanceof Error ? err.message : String(err) });
    } finally {
      setTestRunning(false);
    }
  }

  /** Run the connector's provisioning steps, keeping a transport failure distinct
   *  from a PARTIAL result — one means setup never ran, the other that it did. */
  async function runSetup() {
    setSetupRunning(true);
    setSetupResult(null);
    setSetupError(null);
    try {
      const res = await api.runSetup(id!);
      setSetupResult(res);
    } catch (err: unknown) {
      // Transport/HTTP failure — setup never ran. Keep this distinct from a
      // PARTIAL setup result (which means provisioning ran but some fields failed).
      setSetupError(err instanceof Error ? err.message : String(err));
    } finally {
      setSetupRunning(false);
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Connection"
        title={inst.name ?? inst.id}
        meta={inst.id}
        summary={vendorLabel(inst.connectorType, connectors.data?.connectors ?? [])}
        actions={<ButtonLink to={`/connections/${id}/edit`} variant="secondary">Edit</ButtonLink>}
      />
      <PageHelp />

      {hasTestConnection && (
        <section style={{ marginBottom: 16 }}>
          <div className="uppercase-label" style={{ marginBottom: 6 }}>CONNECTION CHECK</div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Button variant="secondary" onClick={runTest} disabled={testRunning}>
              {testRunning ? "TESTING…" : "TEST CONNECTION"}
            </Button>
            {testResult && testResult.ok && <Badge tone="ok">OK</Badge>}
            {testResult && !testResult.ok && (
              <span className="field__error">{testResult.detail ?? "failed"}</span>
            )}
          </div>
        </section>
      )}

      {hasSetup && (
        <section style={{ marginBottom: 16 }}>
          <div className="uppercase-label" style={{ marginBottom: 6 }}>CUSTOM FIELDS</div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Button variant="secondary" onClick={runSetup} disabled={setupRunning}>
              {setupRunning ? "CHECKING…" : "CHECK CUSTOM FIELDS"}
            </Button>
            {setupResult && setupResult.ok && <Badge tone="ok">OK</Badge>}
            {setupResult && !setupResult.ok && <Badge tone="warn">PARTIAL</Badge>}
            {setupError && <span className="field__error">{setupError}</span>}
            {!setupError && setupResult?.detail && (
              <span className="mono" style={{ fontSize: 12 }}>{setupResult.detail}</span>
            )}
          </div>
          <p className="page-help" style={{ marginTop: 6 }}>
            Idempotently provisions the custom fields this connector writes (e.g. the
            <code className="mono"> jump_* </code> properties on HubSpot deals and contacts).
            Safe to run anytime — existing fields are left untouched.
          </p>
        </section>
      )}

      {pushUrl && (
        <section className="push-panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 className="push-panel__title">PUSH ENDPOINT</h3>
            <Badge tone="ok">{webhook?.signatureScheme ?? "signed"}</Badge>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
            <Code>{pushUrl}</Code>
            <CopyButton value={pushUrl} />
          </div>
          <dl>
            <dt>signature scheme</dt><dd>{webhook?.signatureScheme}</dd>
            <dt>secret name</dt><dd>{secretName}</dd>
          </dl>
          <p className="page-help">
            Webhook signing credentials live in the connector's secret payload.
            Update them via <Link to={`/connections/${id}/edit`}>EDIT</Link> →
            "Rotate credentials".
          </p>
        </section>
      )}

      <div style={{ marginTop: 32 }}>
        <TechnicalDetails title="Connection configuration" copyText={JSON.stringify(inst.config ?? {}, null, 2)}>
          <Code block>{JSON.stringify(inst.config ?? {}, null, 2)}</Code>
        </TechnicalDetails>
      </div>
    </div>
  );
}
